import { resetPushPromptSession } from "../../notifications/pushPromptPreference";
import { roleProfile } from "../../sales/roleProfiles";
import { loadRoleProfiles } from "../../sales/roleProfileRepository";
import { effectiveRole } from "../../sales/roles";
import { detachPush } from "../../notifications/pushClient";
import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";
import { clearSignedFileCache } from "./privateFiles";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      await loadRoleProfiles();
      const sale = await getSale();

      if (sale == null || sale.disabled) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: undefined,
        administrator: sale.administrator,
        hidden_from_team: sale.hidden_from_team,
        role: sale.role ?? "manager",
        rolesRevision: JSON.stringify(roleProfile(sale.role)),
        disabled: sale.disabled,
      };
    },
  });

// To speed up checks, we cache the initialization state
// in local storage. User permissions are always read from the server.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data, error } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  if (error) throw error;
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

const pendingProfiles = new Map<string, ReturnType<typeof readSale>>();

const readSale = async (userId: string) => {
  const { data, error } = await getSupabaseClient()
    .from("sales")
    .select(
      "id,first_name,last_name,administrator,hidden_from_team,role,disabled",
    )
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data ?? undefined;
};

const getSale = async () => {
  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (errorSession) throw errorSession;
  if (dataSession?.session?.user == null) {
    return undefined;
  }

  const userId = dataSession.session.user.id;
  const pending = pendingProfiles.get(userId);
  if (pending) return pending;
  const request = readSale(userId);
  pendingProfiles.set(userId, request);
  try {
    return await request;
  } finally {
    if (pendingProfiles.get(userId) === request) pendingProfiles.delete(userId);
  }
};

function clearCache() {
  clearSignedFileCache();
  pendingProfiles.clear();
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    resetPassword: async (params: { email: string }) =>
      baseAuthProvider.resetPassword!({
        ...params,
        redirectTo: new URL(
          "auth-callback.html",
          new URL(import.meta.env.BASE_URL, window.location.origin),
        ).href,
      }),
    login: async (params) => {
      resetPushPromptSession();
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      resetPushPromptSession();
      await detachPush().catch(() => {});
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      await baseAuthProvider.checkAuth(params);
      const sale = await getSale();
      if (!sale || sale.disabled) {
        clearCache();
        await getSupabaseClient().auth.signOut();
        throw { redirectTo: "/login", message: "Учётная запись отключена" };
      }
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null || sale.disabled) return false;

      // Compute access rights from the sale role
      await loadRoleProfiles();
      const role = effectiveRole(sale);
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
